import Vue from 'vue';
import Vuex, { MutationTree, ActionTree } from 'vuex';

import type { Project } from '@/models/project';
import { ActiveTab } from '@/components/prine/enums/ActiveTab';
import { projectWebservice } from '@/webservices/project';
import type { Company } from '@/models/company';

Vue.use(Vuex);

interface State {
    projects: Project[];
    selectedProject: Project;
    activeTab: ActiveTab;
}

const defaultProject = {
    id: 0,
    admin_id: 0,
    title: '',
    description: '',
    trades: [],
    phases: [],
    pmphases: [],
    xprocesses: [],
    lessons: [],
    users: [],
    external_users: [],
    worksheets_count: 0,
    pm_worksheets_count: 0,
    applied_lessons_count: 0,
    pm_applied_lessons_count: 0,
    all_created_lessons_count: 0,
    ms_team_id: null,
    ms_integration: null,
    company: {} as Company,
    order: undefined,
    parent_project: undefined,
    company_id: 0,
};

const mutations: MutationTree<State> = {
    setProjects(currentState, projects: Project[]) {
        currentState.projects = projects;
    },

    setSelectedProject(currentState, selectedProject: Project) {
        currentState.selectedProject = selectedProject;
    },

    setActiveTab(currentState, activeTab: ActiveTab) {
        currentState.activeTab = activeTab;
    },

    resetState() {
        // console.log('resetState FIRED FROM Account STORE');
        // acquire initial state
        const s = initialState();

        Object.keys(s).forEach((key) => {
            state[key] = s[key];
        });
    },
};

const actions: ActionTree<State, any> = {
    fetchAllProjects({ commit, state: currentState, dispatch }): any {
        return projectWebservice
            .load()
            .then((response) => {
                const projects: Project[] = response.data ? response.data : [];
                commit('setProjects', projects);

                const selected = currentState.selectedProject;
                if (selected) {
                    const stillExists = projects.some((p) => p.id === selected.id);
                    if (!stillExists) {
                        commit('setSelectedProject', defaultProject);
                    }
                }
            })
            .catch((error) => {
                console.error(error);
            });
    },

    setFirstProjectOrNull({ commit, state: localState }): Promise<void> {
        const project = localState.projects.length > 0 ? localState.projects[0] : null;
        if (!project) {
            return Promise.resolve();
        }

        return projectWebservice
            .loadOneByUser(project.id)
            .then((response) => {
                commit('setSelectedProject', response.data);
            })
            .catch((error) => {
                console.error(error);
                throw error;
            });
    },

    // tslint:disable-next-line:no-shadowed-variable
    async refreshSelectedProject({ commit, state }): Promise<void> {
        if (!state.selectedProject) {
            return;
        }

        try {
            const response = await projectWebservice.loadOneByUser(state.selectedProject.id);
            commit('setSelectedProject', response.data);
        } catch (error) {
            console.error(error);
        }
    },
};

const getters = {
    projects(currentState: State): Project[] {
        return currentState.projects;
    },

    selectedProject(currentState: State): Project | undefined {
        return currentState.selectedProject;
    },

    activeTab(currentState: State): ActiveTab {
        return currentState.activeTab;
    },
};

function initialState() {
    return {
        projects: Array<Project>(),
        selectedProject: defaultProject,
        activeTab: ActiveTab.trade,
    };
}

const state: State = initialState();

const module = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
    defaultProject,
};

export default module;
